import { useState } from 'react';
import { useSchedule, useCreateShift, useCancelShift, useUpdateShift } from '../../hooks/useSchedule.js';
import { useJobs } from '../../hooks/useJobs.js';
import { useUsers } from '../../hooks/useUsers.js';
import { useAuth } from '../../hooks/useAuth.js';
import { useError } from '../../components/shared/ErrorBanner.jsx';

// Admin/foreman week view. Rows = supervised crew, columns = 7 days.
// Each cell shows scheduled shifts that day; clicking an empty cell
// opens a "new shift" modal pre-filled with user + date.
//
// Drag-to-create deferred to v2 — table + modal is the smallest viable
// surface and matches the rest of the admin pages' pattern.
export default function AdminSchedulePage() {
  const { isForeman } = useAuth();
  const [weekOffset, setWeekOffset] = useState(0);
  const week = weekRangeOffset(weekOffset);
  const { data: shifts = [], isLoading } = useSchedule({
    target: 'all',
    from: week.from,
    to: week.to,
  });
  const { data: users = [] } = useUsers();
  const { data: jobs = [] } = useJobs('active');
  const [openCell, setOpenCell] = useState(null); // { userId, day }

  const visibleUsers = (users || []).filter(u => u.active);
  const days = Array.from({ length: 7 }, (_, i) => week.from + i * 86400000);

  // Group shifts by user → day index.
  const byCell = new Map();
  for (const s of shifts) {
    const dayIdx = Math.floor((s.start_at - week.from) / 86400000);
    if (dayIdx < 0 || dayIdx > 6) continue;
    const key = `${s.user_id}|${dayIdx}`;
    if (!byCell.has(key)) byCell.set(key, []);
    byCell.get(key).push(s);
  }

  return (
    <div>
      <div className="range-bar">
        <select className="field-select" value={weekOffset} onChange={e => setWeekOffset(Number(e.target.value))}>
          <option value={0}>This week</option>
          <option value={1}>Next week</option>
          <option value={2}>2 weeks ahead</option>
          <option value={3}>3 weeks ahead</option>
          <option value={-1}>Last week</option>
        </select>
        <span style={{ color: 'var(--warm-gray)', fontSize: 11 }}>
          {shifts.length} shift{shifts.length === 1 ? '' : 's'} in window
        </span>
      </div>
      {isLoading ? (
        <div className="loading">Loading…</div>
      ) : (
        <div style={{ overflowX: 'auto' }}>
          <table className="tbl schedule-grid">
            <thead>
              <tr>
                <th>Crew</th>
                {days.map(d => (
                  <th key={d}>{new Date(d).toLocaleDateString(undefined, { weekday: 'short', month: 'short', day: 'numeric' })}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {visibleUsers.map(u => (
                <tr key={u.discord_id}>
                  <td>{u.display_name}</td>
                  {days.map((d, dayIdx) => {
                    const cellShifts = byCell.get(`${u.discord_id}|${dayIdx}`) || [];
                    return (
                      <td key={d} className="schedule-cell">
                        {cellShifts.map(s => <ShiftPill key={s.id} s={s} />)}
                        <button
                          className="schedule-add"
                          onClick={() => setOpenCell({ userId: u.discord_id, day: d })}
                          title="Add shift"
                        >+</button>
                      </td>
                    );
                  })}
                </tr>
              ))}
              {visibleUsers.length === 0 && (
                <tr>
                  <td colSpan={8} style={{ color: 'var(--warm-gray)', padding: 12 }}>
                    {isForeman ? 'No supervised crew assigned.' : 'No active crew.'}
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      )}

      {openCell && (
        <NewShiftModal
          userId={openCell.userId}
          day={openCell.day}
          jobs={jobs}
          onClose={() => setOpenCell(null)}
        />
      )}
    </div>
  );
}

function ShiftPill({ s }) {
  const cancel = useCancelShift();
  const { show } = useError();
  const onCancel = async () => {
    if (!confirm('Cancel this shift?')) return;
    try { await cancel.mutateAsync(s.id); } catch (err) { show(err.message); }
  };
  const time = `${fmtTime(s.start_at)}–${fmtTime(s.end_at)}`;
  return (
    <div className={'schedule-pill status-' + s.status}>
      <span>{time}{s.job_name ? ` · ${s.job_name}` : ''}</span>
      <button className="schedule-pill-x" onClick={onCancel} title="Cancel shift">×</button>
    </div>
  );
}

function NewShiftModal({ userId, day, jobs, onClose }) {
  const create = useCreateShift();
  const { show } = useError();
  const dayIso = new Date(day).toISOString().slice(0, 10);
  const [startTime, setStartTime] = useState('08:00');
  const [endTime, setEndTime] = useState('16:00');
  const [jobId, setJobId] = useState('');
  const [notes, setNotes] = useState('');

  const submit = async (e) => {
    e.preventDefault();
    try {
      const start_at = Date.parse(`${dayIso}T${startTime}:00`);
      const end_at   = Date.parse(`${dayIso}T${endTime}:00`);
      if (!Number.isFinite(start_at) || !Number.isFinite(end_at) || end_at <= start_at) {
        show('End time must be after start time');
        return;
      }
      await create.mutateAsync({
        user_id: userId,
        job_id: jobId || null,
        start_at, end_at,
        notes: notes || null,
      });
      onClose();
    } catch (err) { show(err.message); }
  };

  return (
    <div className="modal-backdrop" onClick={onClose}>
      <form className="modal-panel" onClick={e => e.stopPropagation()} onSubmit={submit}>
        <h3 className="panel-title">
          <span><i className="ph ph-calendar-plus" /> New shift · {new Date(day).toLocaleDateString()}</span>
        </h3>
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8 }}>
          <div className="field">
            <label className="field-label">Start</label>
            <input className="field-input" type="time" value={startTime} onChange={e => setStartTime(e.target.value)} required />
          </div>
          <div className="field">
            <label className="field-label">End</label>
            <input className="field-input" type="time" value={endTime} onChange={e => setEndTime(e.target.value)} required />
          </div>
        </div>
        <div className="field">
          <label className="field-label">Job (optional)</label>
          <select className="field-select" value={jobId} onChange={e => setJobId(e.target.value)}>
            <option value="">— no job —</option>
            {jobs.map(j => <option key={j.id} value={j.id}>{j.name}</option>)}
          </select>
        </div>
        <div className="field">
          <label className="field-label">Notes</label>
          <textarea className="field-input" rows={2} value={notes} onChange={e => setNotes(e.target.value)} />
        </div>
        <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8, marginTop: 12 }}>
          <button type="button" className="btn-sm" onClick={onClose} disabled={create.isPending}>Cancel</button>
          <button type="submit" className="btn-sm primary" disabled={create.isPending}>
            {create.isPending ? 'Saving…' : 'Create'}
          </button>
        </div>
      </form>
    </div>
  );
}

function weekRangeOffset(weeksAgo = 0) {
  const now = new Date();
  const day = (now.getDay() + 6) % 7;
  const start = new Date(now);
  start.setHours(0, 0, 0, 0);
  start.setDate(start.getDate() - day + weeksAgo * 7);
  const end = new Date(start);
  end.setDate(end.getDate() + 7);
  return { from: +start, to: +end };
}

function fmtTime(ms) {
  return new Date(ms).toLocaleTimeString(undefined, { hour: 'numeric', minute: '2-digit' });
}
